/**
 * Demo environment admin client.
 *
 * `resetDemoEnvironment()` is the one-click reset for the Vitalora demo
 * tenant — requires MG role + the caller to already be on the demo tenant.
 *
 * `bootstrapDemoEnvironment()` is the chicken-and-egg solver: it seeds
 * Vitalora on a stage where the migrator has not been invoked. Requires
 * MG role but NOT that the caller is on the demo tenant. Idempotent.
 *
 * `updateDemoBrandName()` rebrands the demo tenant's visible display
 * name (e.g. flip "Vitalora" to "Nintendo" before a customer meeting).
 * Demo-only on the backend; passing an empty string resets back to the
 * seeded default.
 *
 * Backend handlers: `services/christo/app/handlers/catalyst/demo.ts`.
 */
import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

/**
 * Lifecycle states for an async Reset Demo job. Mirrors the backend
 * `DemoResetJobStatus` enum in `packages/commons-node/src/models/
 * demoResetJob.ts`. The settings UI polls until the status lands in
 * a terminal state (`succeeded` / `failed`).
 */
export type DemoResetJobStatus = "queued" | "running" | "succeeded" | "failed";

/**
 * Initial 202-shape response from `POST /demo/reset` — the wipe
 * itself runs out-of-band on the demo-tasks SQS consumer lambda
 * (15-minute timeout) because the synchronous version routinely
 * timed out against API Gateway's 29s sync limit.
 */
export interface EnqueueResetDemoResponse {
  success: true;
  enterpriseId: string;
  jobId: string;
  status: "queued";
}

/**
 * Successful poll-result shape from `GET /demo/reset/status/:jobId`.
 * All dates serialised as ISO-8601 strings.
 */
export interface DemoResetJobStatusResponse {
  success: true;
  jobId: string;
  enterpriseId: string;
  status: DemoResetJobStatus;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BootstrapDemoResponse {
  success: true;
  enterpriseId: string;
  alreadyExisted: boolean;
  completedAt: string;
}

export interface UpdateDemoBrandNameResponse {
  success: true;
  enterpriseId: string;
  name: string;
  updatedAt: string;
}

export async function resetDemoEnvironment(): Promise<EnqueueResetDemoResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/reset`, {
    method: "POST",
  })) as EnqueueResetDemoResponse;
}

export async function getDemoResetJobStatus(
  jobId: string,
): Promise<DemoResetJobStatusResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/reset/status/${encodeURIComponent(jobId)}`,
    { method: "GET" },
  )) as DemoResetJobStatusResponse;
}

export async function bootstrapDemoEnvironment(): Promise<BootstrapDemoResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/bootstrap`, {
    method: "POST",
  })) as BootstrapDemoResponse;
}

export interface SeedDemoChatsResponse {
  success: true;
  enterpriseId: string;
  seededChatCount: number;
  completedAt: string;
}

/**
 * Seed the deterministic Ask Puntt demo chats on demand. Counterpart to
 * Reset Demo, which wipes every chat and does NOT reseed — this is the
 * "second button" that repopulates the canonical fixtures. MG-only +
 * demo-only on the backend; idempotent (upsert by fixed chat ids).
 */
export async function seedDemoChats(): Promise<SeedDemoChatsResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/seed-chats`, {
    method: "POST",
  })) as SeedDemoChatsResponse;
}

/**
 * Rebrand the demo tenant. Pass an empty string (or call
 * `resetDemoBrandName`) to revert to the seeded default ("Vitalora
 * Consumer Brands"). The backend rejects values longer than 80 chars and
 * any value containing an em-dash.
 */
export async function updateDemoBrandName(
  name: string,
): Promise<UpdateDemoBrandNameResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/brand`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  })) as UpdateDemoBrandNameResponse;
}

/** Reset the demo brand name to the seeded default. */
export async function resetDemoBrandName(): Promise<UpdateDemoBrandNameResponse> {
  return updateDemoBrandName("");
}

/**
 * Killer-flow reviewer-function identifiers. Mirrors the backend
 * `DemoReviewFunction` enum (`packages/schemas/src/commons/enterprise.ts`).
 */
export type DemoReviewFunction =
  | "brand"
  | "agency"
  | "regulatory"
  | "medical"
  | "copy";

export const DEMO_REVIEW_FUNCTION_LABELS: Record<DemoReviewFunction, string> = {
  brand: "Brand",
  agency: "Agency",
  regulatory: "Regulatory",
  medical: "Medical",
  copy: "Copy",
};

/**
 * User-added reviewer entry. Additive on top of the five immutable
 * defaults. `id` is a kebab-case slug minted in the FE before save
 * (uniqueness enforced server-side); `label` is whatever the demoer
 * typed; `enabled` mirrors the same UI checkbox the defaults use via
 * `enabledReviewFunctions` membership.
 *
 * The 5 defaults are NEVER allowed to be renamed or removed — only
 * checked / unchecked. Customs are full CRUD. The buyer-facing intent
 * is that scripted-response copy can attribute findings to custom
 * reviewer labels the demoer chose.
 */
export interface DemoCustomReviewFunction {
  id: string;
  label: string;
  enabled: boolean;
}

/** Hard cap on the number of custom reviewers per demo enterprise. */
export const DEMO_CUSTOM_REVIEW_FUNCTIONS_MAX = 20;
export const DEMO_CUSTOM_REVIEW_FUNCTION_LABEL_MAX_LEN = 64;
export const DEMO_CUSTOM_REVIEW_FUNCTION_ID_MAX_LEN = 64;

export interface GetDemoReviewFunctionsResponse {
  success: true;
  enterpriseId: string;
  enabledReviewFunctions: DemoReviewFunction[];
  defaults: DemoReviewFunction[];
  customReviewFunctions: DemoCustomReviewFunction[];
}

export interface UpdateDemoReviewFunctionsResponse {
  success: true;
  enterpriseId: string;
  enabledReviewFunctions: DemoReviewFunction[];
  customReviewFunctions: DemoCustomReviewFunction[];
  updatedAt: string;
}

export async function getDemoReviewFunctions(): Promise<GetDemoReviewFunctionsResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/review-functions`,
    { method: "GET" },
  )) as GetDemoReviewFunctionsResponse;
}

export async function updateDemoReviewFunctions(
  enabledReviewFunctions: DemoReviewFunction[],
  customReviewFunctions?: DemoCustomReviewFunction[],
): Promise<UpdateDemoReviewFunctionsResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/review-functions`,
    {
      method: "PUT",
      body: JSON.stringify(
        customReviewFunctions === undefined
          ? { enabledReviewFunctions }
          : { enabledReviewFunctions, customReviewFunctions },
      ),
    },
  )) as UpdateDemoReviewFunctionsResponse;
}

/**
 * Convert a free-form custom-reviewer label into a kebab-case slug
 * suitable for the `id` field. Strips diacritics, replaces
 * non-alphanumeric runs with single dashes, and lower-cases. The
 * server enforces uniqueness + collision rules; the FE produces a
 * stable starting slug so the testid pattern
 * `demo-review-fn-row-${id}` stays predictable across reloads.
 */
export function toCustomReviewerSlug(label: string): string {
  const ascii = label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  const slug = ascii.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return slug.slice(0, DEMO_CUSTOM_REVIEW_FUNCTION_ID_MAX_LEN) || "reviewer";
}

// ─── Demo pre-run pipeline ───────────────────────────────────────────────────
//
// Phase 2 of the 3-tier asset flow plan. Demo tenant stores N rows of
// MG-edited findings against buyer-specific assets; the killer-flow
// streamer replays them when the same filename is dropped in. Row
// identity = `_id`; uniqueness enforced server-side by
// `(enterprise, asset.name)` so re-dropping the same filename
// replaces the previous row in place.

export type DemoPrerunSeverity = "high" | "medium" | "low" | "n/a";

export type DemoPrerunRiskScore = "low" | "medium" | "high";

export type DemoPrerunState = "pending" | "done" | "error";

export type DemoPrerunSource = "live-ai-prerun" | "canned-snapshot" | "manual";

export interface DemoPrerunFinding {
  reviewerFunction: DemoReviewFunction;
  reviewerLabel: string;
  rule: string;
  text: string;
  source: string;
  severity: DemoPrerunSeverity;
  rel_x: number;
  rel_y: number;
  page_num?: number;
  knowledgeRecordId?: string;
}

export interface DemoPrerunAssetMeta {
  s3Key: string;
  name: string;
  type: "image" | "video" | "file";
  thumbnailUrl?: string;
  size?: number;
}

export interface DemoPrerunAsset {
  id: string;
  state: DemoPrerunState;
  asset: DemoPrerunAssetMeta;
  findings: DemoPrerunFinding[];
  perFunctionFindings?: Partial<
    Record<DemoReviewFunction, DemoPrerunFinding[]>
  >;
  riskScore: DemoPrerunRiskScore;
  source: DemoPrerunSource;
  jobId?: string;
  errorMessage?: string;
  editedAt?: string;
  editedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListDemoPrerunAssetsResponse {
  success: true;
  enterpriseId: string;
  assets: DemoPrerunAsset[];
}

export interface StartDemoPrerunResponse {
  success: true;
  enterpriseId: string;
  id: string;
  state: DemoPrerunState;
  jobId?: string;
}

export interface UpdateDemoPrerunResponse {
  success: true;
  id: string;
  state: DemoPrerunState;
  editedAt: string;
}

export interface DeleteDemoPrerunResponse {
  success: true;
  id: string;
  deleted: boolean;
}

export async function listDemoPrerunAssets(): Promise<ListDemoPrerunAssetsResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/prerun`, {
    method: "GET",
  })) as ListDemoPrerunAssetsResponse;
}

export async function startDemoPrerun(
  asset: DemoPrerunAssetMeta,
): Promise<StartDemoPrerunResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/prerun`, {
    method: "POST",
    body: JSON.stringify({ asset }),
  })) as StartDemoPrerunResponse;
}

export async function updateDemoPrerun(
  id: string,
  body: {
    findings: DemoPrerunFinding[];
    perFunctionFindings?: Partial<
      Record<DemoReviewFunction, DemoPrerunFinding[]>
    >;
    riskScore?: DemoPrerunRiskScore;
  },
): Promise<UpdateDemoPrerunResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/prerun/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  })) as UpdateDemoPrerunResponse;
}

export async function deleteDemoPrerun(
  id: string,
): Promise<DeleteDemoPrerunResponse> {
  return (await smartFetch(`${API_URL}/private/catalyst/demo/prerun/${id}`, {
    method: "DELETE",
  })) as DeleteDemoPrerunResponse;
}

// Re-run the production AI review on an existing pre-run row without
// re-uploading the asset. Returns immediately with `state: "pending"`
// (or `"done"` + errorMessage if Temporal couldn't be reached); the
// next `listDemoPrerunAssets` poll reconciles real findings back in.
export async function retryDemoPrerun(
  id: string,
): Promise<StartDemoPrerunResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/prerun/${id}/retry`,
    {
      method: "POST",
    },
  )) as StartDemoPrerunResponse;
}

// ─── Demo custom canned Ask Puntt responses ──────────────────────────────────
//
// Buyer-tweakable Q&A: triggerPhrases (case-insensitive substring match,
// any phrase fires the response) + the response markdown. The matcher
// checks these BEFORE the hardcoded `SCRIPTED_RESPONSES` table on the
// backend, so they can override hardcoded responses. Reset Demo wipes
// every user entry as part of the `demoConfig` $unset.

export interface DemoScriptedResponse {
  id: string;
  triggerPhrases: string[];
  response: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ListDemoScriptedResponsesResponse {
  success: true;
  enterpriseId: string;
  scriptedResponses: DemoScriptedResponse[];
}

export interface MutateDemoScriptedResponseResponse {
  success: true;
  enterpriseId: string;
  scriptedResponse: DemoScriptedResponse;
}

export interface DeleteDemoScriptedResponseResponse {
  success: true;
  enterpriseId: string;
  id: string;
  deleted: boolean;
}

export async function listDemoScriptedResponses(): Promise<ListDemoScriptedResponsesResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/scripted-responses`,
    { method: "GET" },
  )) as ListDemoScriptedResponsesResponse;
}

export async function createDemoScriptedResponse(body: {
  triggerPhrases: string[];
  response: string;
}): Promise<MutateDemoScriptedResponseResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/scripted-responses`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  )) as MutateDemoScriptedResponseResponse;
}

export async function updateDemoScriptedResponse(
  id: string,
  body: {
    triggerPhrases: string[];
    response: string;
  },
): Promise<MutateDemoScriptedResponseResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/scripted-responses/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  )) as MutateDemoScriptedResponseResponse;
}

export async function deleteDemoScriptedResponse(
  id: string,
): Promise<DeleteDemoScriptedResponseResponse> {
  return (await smartFetch(
    `${API_URL}/private/catalyst/demo/scripted-responses/${id}`,
    { method: "DELETE" },
  )) as DeleteDemoScriptedResponseResponse;
}
